import classNames from 'classnames';
import { compact } from 'lodash';
import React, { useCallback } from 'react';
import { QuestionProps } from '../..';
import {
  convertToScorecards,
  convertToString,
  findScorecard,
  getScore,
} from '../../../utils';
import { QuestionnaireQuestionNotes } from '../../../QuestionnaireQuestionNotes';
import ScorecardGroup from '../../../../../components/ScorecardGroup';
import { Maybe } from '../../../../../../../utils/maybe';

export type ScorecardInputProps = Omit<QuestionProps, 'type'>;

export type Score = Maybe<number>;

export type IScorecard = {
  name: string;
  score: Score;
};

const putScorecard = (scorecards: IScorecard[], name: string, score: Score) => {
  const scorecard = findScorecard(scorecards, name);
  if (scorecard) {
    scorecard.score = score;
  } else {
    scorecards.push({ name, score });
  }
};

const Scorecard: React.FC<ScorecardInputProps> = ({
  value,
  onChange,
  choices,
  notes,
  onChangeNotes,
  readOnly,
  requiredChoices,
  showOptionalComment,
}) => {
  const scorecards = compact(convertToScorecards(value));

  const handleChange = useCallback(
    (score: Score, choice: string) => {
      putScorecard(scorecards, choice, score);
      onChange(convertToString(scorecards));
    },
    [onChange, scorecards],
  );

  return (
    <div className={classNames('d-flex', 'flex-column', 'gap-3')}>
      {choices.map((choice, key) => (
        <div
          className={classNames('d-flex', 'flex-column', 'gap-2')}
          key={`${choice.value}-${key}`}>
          <div>
            <span className="pe-1">{choice.label}</span>
            {requiredChoices.indexOf(choice.value) !== -1 && (
              <abbr className="text-danger me-1">*</abbr>
            )}
          </div>
          <div>
            <ScorecardGroup
              selectedScore={getScore(scorecards, choice.value)}
              name={choice.value}
              disabled={readOnly}
              loading={false}
              isComplete={false}
              onChange={handleChange}
            />
          </div>
        </div>
      ))}
      {showOptionalComment && (
        <QuestionnaireQuestionNotes
          value={notes}
          onChange={onChangeNotes}
          readOnly={readOnly}
        />
      )}
    </div>
  );
};

export default Scorecard;
